import { createBrowserRouter, Navigate, createRoutesFromElements, Route } from 'react-router-dom';

import ProtectedRoute from './components/ProtectedRoute';

import Login from './pages/Login';
import FacebookCallback from './pages/FacebookCallback';
import InstagramCallback from './pages/InstagramCallback';
import MailchimpCallback from './features/channel/pages/MailchimpCallback';
import RegisterStep1 from './pages/RegisterStep1';
import RegisterStep2 from './pages/RegisterStep2';
import RegisterOAuth from './pages/RegisterOAuth';
import ForgotPassword from './pages/ForgotPassword';
import ForgotPasswordThankYou from './pages/ForgotPasswordThankYou';
import ResetPassword from './pages/ResetPassword';

import BackOfficeHome from './backOffice/pages/Home';
import BackOfficeLayout from './backOffice/components/Layout';
import BackOfficeLogin from './backOffice/pages/Login';
import BackOfficeProtectedRoute from './backOffice/components/ProtectedRoute';
import BackOfficeResetPassword from './backOffice/pages/ResetPassword';
import Firms from './backOffice/pages/firm/Firms';
import Firm from './backOffice/pages/firm/Firm';
import BillingMetrics from './backOffice/pages/tracking/BillingMetrics'
import PromptTemplates from './backOffice/pages/prompt/PromptTemplates';
import PromptTemplate from './backOffice/pages/prompt/PromptTemplate';
import SystemPrompts from './backOffice/pages/prompt/SystemPrompts';
import AgentPrompts from './backOffice/pages/prompt/AgentPrompts';
import InstructionBlocks from './backOffice/pages/instructionBlock/InstructionBlocks';
import InstructionBlock from './backOffice/pages/instructionBlock/InstructionBlock';
import ResolvedPrompts from './backOffice/pages/instructionBlock/ResolvedPrompts';
import BackOfficeContentTypes from './backOffice/pages/ContentTypes'
import ContentValidators from './backOffice/pages/validator/ContentValidators';
import ContentValidatorEdit from './backOffice/pages/validator/ContentValidatorEdit';
import SupportRequests from './backOffice/pages/support/SupportRequests';
import SupportRequest from './backOffice/pages/support/SupportRequest';
import BackofficeUsers from './backOffice/pages/user/BackofficeUsers';
import BackofficeUser from './backOffice/pages/user/BackofficeUser';
import BackOfficeSettings from './backOffice/pages/Settings';
import BackOfficeChangePassword from './backOffice/pages/ChangePassword';
import FirmUser from './backOffice/pages/firm/FirmUser';

import { appRoutes } from './app/routes';
import LegacyV2Redirect from './app/LegacyV2Redirect';
import APP_ROUTES from './app/routePaths';

import * as constants from './constants';

import App from './app/App';
import ErrorBoundary from './components/ErrorBoundary';
import SystemSettings from './backOffice/pages/systemsettings/SystemSettings';

const renderRouteObjects = (routes, prefix = 'route') => (
  routes.map((route, index) => {
    const routeKey = route.path ?? `${prefix}-${index}`;
    return (
      <Route key={routeKey} path={route.path} element={route.element}>
        {route.children ? renderRouteObjects(route.children, routeKey) : null}
      </Route>
    );
  })
);

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<ErrorBoundary><App /></ErrorBoundary>}>
      {}
      <Route path={constants.ROUTE_BACK_OFFICE_LOGIN} element={<BackOfficeLogin />} />
      <Route path={constants.ROUTE_BACK_OFFICE_RESET_PASSWORD} element={<BackOfficeResetPassword />} />
      <Route element={<BackOfficeProtectedRoute permissions={null} />}>
        <Route element={<BackOfficeLayout />}>
          <Route path={constants.ROUTE_FIRMS} element={<Firms />} />
          <Route path={constants.ROUTE_FIRM_EDIT} element={<Firm />} />
          <Route path={constants.ROUTE_FIRM_USER_EDIT} element={<FirmUser />} />
          <Route path={constants.ROUTE_BILLING_METRICS} element={<BillingMetrics />} />
          <Route path={constants.ROUTE_BACK_OFFICE_HOME} element={<BackOfficeHome />} />
          <Route path={constants.ROUTE_BACK_OFFICE_USERS} element={<BackofficeUsers />} />
          <Route path={constants.ROUTE_BACK_OFFICE_USER} element={<BackofficeUser />} />
          <Route path={constants.ROUTE_BACK_OFFICE_PROMPT_TEMPLATES} element={<PromptTemplates />} />
          <Route path={constants.ROUTE_BACK_OFFICE_PROMPT_TEMPLATE} element={<PromptTemplate />} />
          <Route path={constants.ROUTE_BACK_OFFICE_SYSTEM_PROMPTS} element={<SystemPrompts />} />
          <Route path={constants.ROUTE_BACK_OFFICE_INSTRUCTION_BLOCKS} element={<InstructionBlocks />} />
          <Route path={constants.ROUTE_BACK_OFFICE_INSTRUCTION_BLOCK} element={<InstructionBlock />} />
          <Route path={constants.ROUTE_BACK_OFFICE_RESOLVED_PROMPTS} element={<ResolvedPrompts />} />
          <Route path={constants.ROUTE_BACK_OFFICE_CONTENT_TYPES} element={<BackOfficeContentTypes />} />
          <Route path={constants.ROUTE_BACK_OFFICE_CONTENT_VALIDATORS} element={<ContentValidators />} />
          <Route path={constants.ROUTE_BACK_OFFICE_CONTENT_VALIDATOR} element={<ContentValidatorEdit />} />
          <Route path={constants.ROUTE_BACK_OFFICE_AGENT_PROMPTS} element={<AgentPrompts />} />
          <Route path={constants.ROUTE_BACK_OFFICE_SUPPORT_REQUESTS} element={<SupportRequests />} />
          <Route path={constants.ROUTE_BACK_OFFICE_SUPPORT_REQUEST} element={<SupportRequest />} />
          <Route path={constants.ROUTE_BACK_OFFICE_SETTINGS} element={<BackOfficeSettings />} />
          <Route path={constants.ROUTE_BACK_OFFICE_CHANGE_PASSWORD} element={<BackOfficeChangePassword />} />
          <Route path={constants.ROUTE_BACK_OFFICE_SYSTEM_SETTINGS} element={<SystemSettings />} />
        </Route>
      </Route>
      <Route path="/bo/*" element={<Navigate to={constants.ROUTE_BACK_OFFICE_HOME} replace />} />

      {}
      <Route path={constants.ROUTE_LOGIN} element={<Login />} />
      <Route path={constants.ROUTE_REGISTER_USER} element={<RegisterStep1 />} />
      <Route path={constants.ROUTE_REGISTER_FIRM} element={<RegisterStep2 />} />
      <Route path={constants.ROUTE_REGISTER_FIRM_OAUTH} element={<RegisterOAuth />} />

      <Route path={constants.ROUTE_FORGOT_PASSWORD} element={<ForgotPassword />} />
      <Route path={constants.ROUTE_FORGOT_PASSWORD_THANK_YOU} element={<ForgotPasswordThankYou />} />
      <Route path={constants.ROUTE_RESET_PASSWORD} element={<ResetPassword />} />

      <Route path={constants.ROUTE_FACEBOOK_CALLBACK} element={<FacebookCallback />} />
      <Route path={constants.ROUTE_INSTAGRAM_CALLBACK} element={<InstagramCallback />} />
      <Route path={constants.ROUTE_MAILCHIMP_CALLBACK} element={<MailchimpCallback />} />

      {}
      <Route element={<ProtectedRoute />}>
        <Route path={constants.ROUTE_HOME} element={<Navigate to={APP_ROUTES.DASHBOARD} replace />} />
        {renderRouteObjects(appRoutes, 'app')}
      </Route>

      <Route path="/v2/*" element={<LegacyV2Redirect />} />
    </Route>
  ),
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true
    }
  }
);